import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Shield, ChevronDown, ChevronUp } from "lucide-react";

const BAND = {
  low:      { c: "text-emerald-700 bg-emerald-50 border-emerald-200", l: "Low risk" },
  medium:   { c: "text-amber-800 bg-amber-50 border-amber-200", l: "Medium risk" },
  high:     { c: "text-red-800 bg-red-50 border-red-200", l: "High risk" },
  critical: { c: "text-red-900 bg-red-100 border-red-300", l: "Critical" },
};

const SEV_DOT = {
  critical: "bg-red-500",
  high: "bg-red-400",
  medium: "bg-amber-500",
  low: "bg-blue-400",
  info: "bg-slate-300",
};

/** Compact risk chip + collapsible factor list on the Booking Detail page. */
export default function BookingRiskChip({ bookingId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}/risk`);
        setData(data);
      } catch (_) { /* hide on failure */ }
    })();
  }, [bookingId]);

  if (!data) return null;
  const band = BAND[data.band] || BAND.low;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="booking-risk-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#1B4332]" />
          <span className="font-display font-semibold">Booking risk</span>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${band.c}`}
                data-testid="risk-band">{band.l}</span>
          <span className="text-sm text-slate-500" data-testid="risk-score">· {data.score}/100</span>
        </div>
        {data.factors.length > 0 && (
          <button onClick={() => setOpen((v) => !v)}
                  data-testid="risk-toggle"
                  className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {open ? "Hide" : `Why? (${data.factors.length})`}
          </button>
        )}
      </div>

      {open && data.factors.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm" data-testid="risk-factors">
          {data.factors.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-slate-700">
              <span className={`w-2 h-2 rounded-full mt-1.5 ${SEV_DOT[f.severity] || SEV_DOT.info}`} />
              <span className="flex-1">
                <b className="text-slate-900">{f.reason}</b>
                <span className="text-slate-500"> · −{f.deduction} pts</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
